import { useMemo, useState } from 'react'

import cn from 'classnames'
import { Loader2 } from 'lucide-react'
import Modal from 'react-modal'

import { assetApiV2GetTokenInfo } from '@src/api/asset.v2.api'
import ChainToken from '@src/components/ChainToken'
import SearchIcon from '@src/components/icons/Search'
import { CHAIN_IDS, ChainId, ChainName, getChainNameFromChainId } from '@src/configs/chainInfo'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useAvailableTokens } from '@src/pages/Exchange/hooks/useAvailableTokens'
import { Address, IToken, SolanaAddress, TronAddress } from '@src/types/token'
import { getChecksumAddress, isEVMAddress, isSolanaAddress, isTronAddress } from '@src/utils/address'
import { Balance } from '@src/utils/balance'
import { formatNumber } from '@src/utils/formatFee'
import { useTokensStore } from '@src/zustand_stores'

interface ISelectTokenModalProps {
    isOpen: boolean
    onClose: () => void
    availableChains: Record<string, any>
    selectedChain: string | number
    setSelectedChain: (__chainId: string | number) => void
    selectedToken?: IToken
    setSelectedToken: (token: IToken) => void
    disabledChains?: number[]
    isSolanaSwap: boolean
    side: 'buy' | 'sell'
}

interface SearchProgressBarProps {
    completed: number
    total: number
}

interface CompactTokensListItemProps {
    selectedToken?: IToken
    token: IToken
    onTokenClicked: (token: IToken) => void
    userBalanceInTokens?: string
    userBalanceInUsd?: string
}

interface CompactTokensListProps {
    tokens: IToken[]
    selectedToken?: IToken
    onTokenClicked: (token: IToken) => void
    getTokenBalance?: (token: IToken) => { tokenBalance: string; usdBalance: string } | undefined
}

const SearchProgressBar: React.FC<SearchProgressBarProps> = ({ completed, total }) => {
    return (
        <div className='relative h-5 w-full'>
            <div className='absolute inset-0 rounded-full bg-gray-500 dark:bg-gray-700'>
                <div
                    className='h-full transform rounded-full bg-blue-600 transition-all duration-300 ease-in-out'
                    style={{ width: `${(completed / total) * 100}%` }}
                />
            </div>
            <div className='absolute inset-0 flex items-center justify-center'>
                <span className='text-xs font-bold text-white transition-opacity duration-200'>
                    {completed}/{total}
                </span>
            </div>
        </div>
    )
}

// Create a compact version of the TokensListItem component
const CompactTokensListItem: React.FC<CompactTokensListItemProps> = ({
    selectedToken,
    token,
    onTokenClicked,
    userBalanceInTokens,
    userBalanceInUsd,
}) => {
    const isSelected = token.address === selectedToken?.address && token.chainId === selectedToken?.chainId

    return (
        <button
            className={cn(
                'flex w-full items-center gap-3 rounded-md px-4 py-3.5 transition-colors duration-200 hover:bg-token-modal-chain-hover',
                isSelected && 'border border-token-modal-border bg-token-modal-chain-selected'
            )}
            onClick={() => onTokenClicked(token)}
        >
            <ChainToken
                tokenLogo={token.name === 'BitTorrent' ? '/assets/token/btt.png' : token.logoURI}
                networkIcon={`/assets/network/${getChainNameFromChainId(token.chainId)}.png`}
                tokenIconSize={28}
                chainIconSize={14}
            />
            <div className='flex flex-1 flex-col items-start gap-0.5 overflow-hidden'>
                <span className='text-left text-[14px] font-bold'>{token.symbol}</span>
                <span className='overflow-hidden text-ellipsis text-left text-xs text-token-modal-label-text'>
                    {token.name}
                </span>
            </div>
            <div className='flex items-center gap-2'>
                {userBalanceInTokens && (
                    <div className='flex flex-col items-end'>
                        {userBalanceInUsd && (
                            <span className='text-[14px] font-medium'>
                                {userBalanceInUsd.startsWith('<')
                                    ? userBalanceInUsd
                                    : `$${formatNumber(userBalanceInUsd)}`}
                            </span>
                        )}
                        <span className='text-xs text-token-modal-label-text'>{userBalanceInTokens}</span>
                    </div>
                )}
                {isSelected && <div className='h-2 w-2 shrink-0 rounded-full bg-token-modal-accent-blue' />}
            </div>
        </button>
    )
}

// Create a compact version of the TokensList component
const CompactTokensList: React.FC<CompactTokensListProps> = ({
    tokens,
    selectedToken,
    onTokenClicked,
    getTokenBalance,
}) => {
    return (
        <div className='space-y-0.5'>
            {tokens.map(token => {
                const balance = getTokenBalance?.(token)

                return (
                    <CompactTokensListItem
                        key={`${token.chainId}-${token.address}`}
                        selectedToken={selectedToken}
                        token={token}
                        onTokenClicked={onTokenClicked}
                        userBalanceInTokens={balance?.tokenBalance}
                        userBalanceInUsd={balance?.usdBalance}
                    />
                )
            })}
        </div>
    )
}

const SelectTokenModal = ({
    isOpen,
    onClose,
    availableChains,
    selectedChain,
    setSelectedChain,
    selectedToken,
    setSelectedToken,
    disabledChains = [],
    isSolanaSwap,
    side,
}: ISelectTokenModalProps): JSX.Element => {
    const [activeChainSelection, setActiveChainSelection] = useState<number>()

    const [tokenQuery, setTokenQuery] = useState('')
    const [searchResultsChain, setSearchResultsChain] = useState<Record<string, any>>({})
    const [searchResult, setSearchResult] = useState<IToken[]>([])
    const [highlightedChain, setHighlightedChain] = useState<number>()
    const [isSearchingAllChains, setIsSearchingAllChains] = useState(false)
    const [searchProgress, setSearchProgress] = useState({ completed: 0, total: 0 })

    const { tokens: storedTokens } = useTokensStore()
    const { isTronConnected, isSolanaConnected, isEVMConnected } = useWalletConnections()

    const getTokenBalance = (token: IToken) => {
        if (!token.userBalance || !token.userBalanceInUSD) return undefined

        // Only show balances for connected wallets on their respective chains
        if (
            (token.chainId === ChainId.Tron && !isTronConnected) ||
            (token.chainId === ChainId.Solana && !isSolanaConnected) ||
            (!isTronConnected && !isSolanaConnected && !isEVMConnected)
        ) {
            return undefined
        }

        // Format balance with reasonable precision
        const balance = new Balance(token).setValue(token.userBalance)
        const formattedBalance = balance.toNumber()

        // For small balances (less than 0.01), show more decimals to avoid showing 0
        const tokenBalance =
            formattedBalance < 0.0001 ? `<0.0001 ${token.symbol}` : `${formattedBalance.toFixed(4)} ${token.symbol}`

        // Format USD value with exactly 4 decimal places
        const usdValue = token.userBalanceInUSD
        const formattedUSD = usdValue < 0.01 ? '<$0.01' : usdValue < 1 ? usdValue.toFixed(4) : usdValue.toFixed(2)

        return {
            tokenBalance: tokenBalance,
            usdBalance: formattedUSD,
        }
    }

    const onChainClicked = (chainId: number): void => {
        // Update both the selected chain and active chain selection
        setSelectedChain(chainId)
        setActiveChainSelection(chainId)
        setHighlightedChain(chainId)

        // If we have an active token search, don't clear it
        // This allows viewing the searched token on the specific chain
        if (!tokenQuery) {
            setTokenQuery('')
        }
    }

    const onTokenClicked = (token: IToken): void => {
        // Ensure we update both the chain and token selection
        setSelectedChain(token.chainId)
        setSelectedToken(token)
        handleClose(token)
    }

    const handleClose = (token?: IToken): void => {
        // Clear all settings when modal is closed
        setActiveChainSelection(undefined)
        setTokenQuery('')
        setHighlightedChain(undefined)
        setSearchResult([])
        setSearchResultsChain({})

        if (token) {
            // Make sure we update both chain and token before closing
            setSelectedToken(token)
            setSelectedChain(token.chainId)
        }

        onClose()
    }

    // Use the active chain selection for filtering if available, otherwise fall back to selected chain
    const { tokens } = useAvailableTokens(activeChainSelection ?? selectedChain, isSolanaSwap, side === 'buy')

    const searchTokenByAddress = async (address: string) => {
        setIsSearchingAllChains(true)

        // Determine which networks to search based on address format
        let networksToSearch: ChainName[] = []
        if (isSolanaAddress(address)) {
            networksToSearch = ['solana']
        } else if (isTronAddress(address)) {
            networksToSearch = ['tron']
        } else if (isEVMAddress(address)) {
            // Search all EVM networks
            networksToSearch = Object.keys(CHAIN_IDS).filter(
                network =>
                    CHAIN_IDS[network as ChainName] !== ChainId.Solana &&
                    CHAIN_IDS[network as ChainName] !== ChainId.Tron
            ) as ChainName[]
        } else {
            // Invalid address format
            setIsSearchingAllChains(false)
            return
        }

        setSearchProgress({ completed: 0, total: networksToSearch.length })

        const results: IToken[] = []
        let completedChains = 0
        const foundChains: Record<string, any> = {}

        for (const networkName of networksToSearch) {
            try {
                const chainId = CHAIN_IDS[networkName]
                const tokenInfo = await assetApiV2GetTokenInfo(getChainNameFromChainId(chainId), address)

                if (tokenInfo) {
                    let tokenAddress = address
                    if (chainId === ChainId.Solana) {
                        tokenAddress = address as SolanaAddress
                    } else if (chainId === ChainId.Tron) {
                        tokenAddress = address as TronAddress
                    } else {
                        // For EVM chains, use checksum address for the logo URL
                        tokenAddress = getChecksumAddress(address)
                    }

                    // Add the chain to foundChains if token is found
                    foundChains[chainId] = availableChains[chainId]

                    results.push({
                        symbol: tokenInfo.symbol,
                        name: tokenInfo.name,
                        address: address as Address,
                        decimals: tokenInfo.decimals,
                        chainId: chainId,
                        // Use Trust Wallet repository for token logos
                        logoURI: `https://raw.githubusercontent.com/trustwallet/assets/refs/heads/master/blockchains/${getChainNameFromChainId(chainId)}/assets/${tokenAddress}/logo.png`,
                        native: false,
                        wrapped: false,
                        isStableCoin: false,
                    })
                }
            } catch (error) {
                console.error(`Error fetching token info for network ${networkName}:`, error)
            }

            completedChains++
            setSearchProgress({ completed: completedChains, total: networksToSearch.length })
        }

        setSearchResult(results)
        // Update the searchResultsChain with only the chains where tokens were found
        setSearchResultsChain(foundChains)
        setIsSearchingAllChains(false)
    }

    // Helper function to compare gas tokens priority
    const compareGasTokens = (a: IToken, b: IToken): number => {
        // Native tokens (like ETH) should come first
        if (a.native && !b.native) return -1
        if (!a.native && b.native) return 1

        // Then wrapped versions (like WETH)
        if (a.wrapped && !b.wrapped) return -1
        if (!a.wrapped && b.wrapped) return 1

        return 0
    }

    const displayedTokens = useMemo(() => {
        if (!tokenQuery) return tokens

        // Check if the input looks like a token address
        const trimmedQuery = tokenQuery.trim()
        if (
            trimmedQuery &&
            (isEVMAddress(trimmedQuery) || isSolanaAddress(trimmedQuery) || isTronAddress(trimmedQuery))
        ) {
            return [] // Return empty array while searching for token address
        }

        // If searching by name, search across all chains
        const query = tokenQuery.toLowerCase()

        // If activeChainSelection is set, filter by both search query and chain
        if (activeChainSelection) {
            return storedTokens
                .filter(
                    token =>
                        (token.name.toLowerCase().includes(query) || token.symbol.toLowerCase().includes(query)) &&
                        Number(token.chainId) === activeChainSelection
                )
                .sort((a, b) => {
                    // First prioritize gas tokens
                    const gasTokenComparison = compareGasTokens(a, b)
                    if (gasTokenComparison !== 0) return gasTokenComparison

                    // Then sort by exact symbol match
                    const aExactMatch = a.symbol.toLowerCase() === query
                    const bExactMatch = b.symbol.toLowerCase() === query
                    if (aExactMatch && !bExactMatch) return -1
                    if (!aExactMatch && bExactMatch) return 1

                    // Then sort by user balance
                    const aBalance = a.userBalanceInUSD ?? 0
                    const bBalance = b.userBalanceInUSD ?? 0
                    if (aBalance && bBalance) {
                        return bBalance - aBalance
                    } else if (aBalance) {
                        return -1
                    } else if (bBalance) {
                        return 1
                    }

                    // Finally sort by market cap
                    const aCap = a.market_cap ?? -Infinity
                    const bCap = b.market_cap ?? -Infinity
                    return bCap - aCap
                })
        }

        // Filter across all chains
        const filteredTokens = storedTokens.filter(
            token => token.name.toLowerCase().includes(query) || token.symbol.toLowerCase().includes(query)
        )

        // Group tokens by name/symbol for better organization
        const sortedTokens = filteredTokens.sort((a, b) => {
            // First prioritize gas tokens
            const gasTokenComparison = compareGasTokens(a, b)
            if (gasTokenComparison !== 0) return gasTokenComparison

            // Then sort by exact symbol match
            const aExactMatch = a.symbol.toLowerCase() === query
            const bExactMatch = b.symbol.toLowerCase() === query
            if (aExactMatch && !bExactMatch) return -1
            if (!aExactMatch && bExactMatch) return 1

            // Then sort by user balance
            const aBalance = a.userBalanceInUSD ?? 0
            const bBalance = b.userBalanceInUSD ?? 0
            if (aBalance && bBalance) {
                return bBalance - aBalance
            } else if (aBalance) {
                return -1
            } else if (bBalance) {
                return 1
            }

            // Finally sort by market cap
            const aCap = a.market_cap ?? -Infinity
            const bCap = b.market_cap ?? -Infinity
            return bCap - aCap
        })

        return sortedTokens
    }, [tokens, tokenQuery, storedTokens, activeChainSelection])

    const onTokenSearchChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const newValue = e.target.value

        // Clear chain selection and results only when starting a new search or clearing an existing search
        if ((tokenQuery && !newValue) || (!tokenQuery && newValue)) {
            setActiveChainSelection(undefined)
            setHighlightedChain(undefined)
        }

        setTokenQuery(newValue)
        setSearchResultsChain({})

        // Check if the input looks like a token address
        const trimmedQuery = newValue.trim()
        if (
            trimmedQuery &&
            (isEVMAddress(trimmedQuery) || isSolanaAddress(trimmedQuery) || isTronAddress(trimmedQuery))
        ) {
            await searchTokenByAddress(trimmedQuery)
        } else if (trimmedQuery) {
            // For name-based search
            const query = newValue.toLowerCase()

            // Filter tokens by name or symbol
            const filteredTokens = storedTokens.filter(
                token => token.name.toLowerCase().includes(query) || token.symbol.toLowerCase().includes(query)
            )

            // Get unique chains that have matching tokens
            const relevantChainIds = Array.from(new Set(filteredTokens.map(token => token.chainId)))
            const filteredChains = Object.entries(availableChains).reduce(
                (filtered, [id, chain]) => {
                    if (relevantChainIds.includes(Number(id))) {
                        filtered[id] = chain
                    }
                    return filtered
                },
                {} as Record<string, any>
            )

            // Sort and update results
            const sortedTokens = filteredTokens.sort((a, b) => {
                // First prioritize gas tokens
                const gasTokenComparison = compareGasTokens(a, b)
                if (gasTokenComparison !== 0) return gasTokenComparison

                // Then sort by exact symbol match
                const aExactMatch = a.symbol.toLowerCase() === query
                const bExactMatch = b.symbol.toLowerCase() === query
                if (aExactMatch && !bExactMatch) return -1
                if (!aExactMatch && bExactMatch) return 1

                // Then sort by user balance
                const aBalance = a.userBalanceInUSD ?? 0
                const bBalance = b.userBalanceInUSD ?? 0
                if (aBalance && bBalance) {
                    return bBalance - aBalance
                } else if (aBalance) {
                    return -1
                } else if (bBalance) {
                    return 1
                }

                // Finally sort by market cap
                const aCap = a.market_cap ?? -Infinity
                const bCap = b.market_cap ?? -Infinity
                return bCap - aCap
            })

            setSearchResultsChain(filteredChains)
            setSearchResult(sortedTokens)
            setIsSearchingAllChains(false)
        } else {
            // Clear results if search is empty
            setSearchResult([])
            setSearchResultsChain({})
            setIsSearchingAllChains(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={() => handleClose(undefined)}
            style={{
                overlay: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'var(--token-modal-overlay)',
                    zIndex: 50,
                    padding: '16px',
                },
                content: {
                    top: '50%',
                    left: '50%',
                    right: 'auto',
                    bottom: 'auto',
                    transform: 'translate(-50%, -50%)',
                    width: '95%',
                    maxWidth: '576px',
                    height: '680px',
                    padding: 0,
                    border: '1px solid var(--token-modal-border)',
                    borderWidth: '1px',
                    borderRadius: '0.375rem',
                    background: 'var(--token-modal-background)',
                    overflow: 'hidden',
                },
            }}
            ariaHideApp={false}
        >
            {/* Mobile View */}
            <div className='flex h-full flex-col sm:hidden'>
                {/* Mobile Header */}
                <div className='flex items-center justify-between p-4'>
                    <h2 className='text-[20px] font-semibold'>Select asset</h2>
                    <button
                        onClick={onClose}
                        className='text-white/70 transition-colors hover:text-white'
                        type='button'
                    >
                        <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path
                                d='M18 6L6 18M6 6L18 18'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                        </svg>
                    </button>
                </div>

                {/* Mobile Search */}
                <div className='px-4 pb-4'>
                    <div className='flex w-full items-center gap-2 rounded-md border border-token-modal-border bg-token-modal-input-bg px-3 py-2.5 transition-all duration-200'>
                        <SearchIcon className='h-5 w-5 text-token-modal-input-placeholder' />
                        <input
                            type='text'
                            placeholder='Search by symbol, name or address'
                            value={tokenQuery}
                            onChange={onTokenSearchChange}
                            className='w-full bg-transparent text-white placeholder-token-modal-input-placeholder outline-none'
                        />
                    </div>
                </div>

                {/* Mobile Chains Section */}
                <div className='px-4 pb-3'>
                    <h3 className='mb-2 text-[20px] font-semibold'>Chains</h3>
                    <div className='grid grid-cols-4 gap-1.5'>
                        {Object.entries(tokenQuery ? searchResultsChain : availableChains)
                            .sort(([networkIdA], [networkIdB]) => {
                                if (+networkIdA === +selectedChain) return -1
                                if (+networkIdB === +selectedChain) return 1
                                return 0
                            })
                            .map(([networkId, chain]) => (
                                <button
                                    key={`network-mobile-${networkId}`}
                                    onClick={() => onChainClicked(+networkId)}
                                    disabled={disabledChains?.includes(+networkId) && +networkId !== +selectedChain}
                                    className={`flex flex-col items-center justify-center gap-0.5 rounded-md px-1 py-1.5 transition-colors duration-200 ${
                                        +networkId === +selectedChain
                                            ? 'border border-token-modal-border bg-token-modal-chain-selected'
                                            : 'hover:bg-token-modal-chain-hover'
                                    }`}
                                    type='button'
                                >
                                    <img src={chain.icon} alt={chain.name} className='h-6 w-6 rounded-full' />
                                    <span className='w-full truncate text-center text-[10px] font-medium'>
                                        {chain.name}
                                    </span>
                                </button>
                            ))}
                    </div>
                </div>

                {/* Mobile Assets Section */}
                <div className='flex-1 overflow-y-auto px-4'>
                    <h3 className='mb-4 text-[20px] font-semibold'>Assets</h3>
                    {isSearchingAllChains ? (
                        <div className='flex flex-col items-center justify-center gap-4 p-8'>
                            <div className='flex items-center gap-2'>
                                <Loader2 className='h-5 w-5 animate-spin' />
                                <span>Searching all blockchains...</span>
                            </div>
                            <SearchProgressBar completed={searchProgress.completed} total={searchProgress.total} />
                        </div>
                    ) : tokenQuery ? (
                        <div className='animate-fadeIn'>
                            <div className='px-2 pb-2'>
                                <h3 className='animate-slideInRight text-sm font-medium text-token-modal-label-text'>
                                    {`Results for "${tokenQuery}" on ${(activeChainSelection !== undefined && availableChains[activeChainSelection]?.name) || 'selected chain'}`}
                                </h3>
                            </div>
                            <CompactTokensList
                                tokens={displayedTokens}
                                selectedToken={selectedToken}
                                onTokenClicked={onTokenClicked}
                                getTokenBalance={getTokenBalance}
                            />
                        </div>
                    ) : (
                        // Show categorized tokens - "Your Tokens" and "All Tokens"
                        <div className='space-y-4'>
                            {/* Tokens with balances */}
                            {displayedTokens.some(token => token.userBalanceInUSD && token.userBalanceInUSD > 0) && (
                                <div className='space-y-1'>
                                    <div className='px-2'>
                                        <h3 className='animate-slideInRight text-sm font-medium text-token-modal-label-text'>
                                            Your Tokens
                                        </h3>
                                    </div>
                                    <CompactTokensList
                                        tokens={displayedTokens
                                            .filter(token => token.userBalanceInUSD && token.userBalanceInUSD > 0)
                                            .sort((a, b) => {
                                                // First prioritize gas tokens
                                                const gasTokenComparison = compareGasTokens(a, b)
                                                if (gasTokenComparison !== 0) return gasTokenComparison

                                                // Then sort by USD balance
                                                return (b.userBalanceInUSD ?? 0) - (a.userBalanceInUSD ?? 0)
                                            })}
                                        selectedToken={selectedToken}
                                        onTokenClicked={onTokenClicked}
                                        getTokenBalance={getTokenBalance}
                                    />
                                </div>
                            )}

                            {/* All tokens */}
                            <div className='space-y-1'>
                                <div className='px-2'>
                                    <h3 className='animate-slideInRight text-sm font-medium text-token-modal-label-text'>
                                        All Tokens
                                    </h3>
                                </div>
                                <CompactTokensList
                                    tokens={displayedTokens
                                        .filter(token => !token.userBalanceInUSD || token.userBalanceInUSD === 0)
                                        .sort((a, b) => {
                                            // First prioritize gas tokens
                                            const gasTokenComparison = compareGasTokens(a, b)
                                            if (gasTokenComparison !== 0) return gasTokenComparison

                                            // Then sort by market cap
                                            const aCap = a.market_cap ?? -Infinity
                                            const bCap = b.market_cap ?? -Infinity
                                            return bCap - aCap
                                        })}
                                    selectedToken={selectedToken}
                                    onTokenClicked={onTokenClicked}
                                    getTokenBalance={getTokenBalance}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Desktop View */}
            <div className='hidden h-full flex-col sm:flex'>
                {/* New Header Structure with Chains and Assets */}
                <div className='flex'>
                    <div className='w-[192px] border-r border-token-modal-divider-dark p-4'>
                        <h2 className='text-[20px] font-semibold'>Chains</h2>
                    </div>
                    <div className='flex flex-1 items-center justify-between p-4'>
                        <h2 className='text-[20px] font-semibold'>Assets</h2>
                        <button
                            onClick={onClose}
                            className='text-white/70 transition-colors hover:text-white'
                            type='button'
                        >
                            <svg
                                width='24'
                                height='24'
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M18 6L6 18M6 6L18 18'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className='flex min-h-0 flex-1 flex-col sm:flex-row'>
                    {/* Left panel - Chains */}
                    <div className='w-[192px] flex-col border-r border-token-modal-divider-dark'>
                        <div className='no-scrollbar h-full overflow-y-auto'>
                            <div className='space-y-1.5 px-3 pt-1'>
                                {Object.entries(
                                    // Show searchResultsChain when searching tokens,
                                    // Show all availableChains when no search
                                    tokenQuery ? searchResultsChain : availableChains
                                )
                                    .sort(([networkIdA], [networkIdB]) => {
                                        if (+networkIdA === +selectedChain) return -1
                                        if (+networkIdB === +selectedChain) return 1
                                        return 0
                                    })
                                    .map(([networkId, chain]) => (
                                        <button
                                            key={`network-${networkId}`}
                                            onClick={() => onChainClicked(+networkId)}
                                            disabled={
                                                disabledChains?.includes(+networkId) && +networkId !== +selectedChain
                                            }
                                            className={`flex w-full items-center gap-2 rounded-md px-3 py-2 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${
                                                +networkId === +selectedChain
                                                    ? 'border border-token-modal-border bg-token-modal-chain-selected'
                                                    : highlightedChain === +networkId
                                                      ? 'bg-token-modal-chain-selected'
                                                      : 'hover:bg-token-modal-chain-hover'
                                            } ${tokenQuery && activeChainSelection === +networkId && 'border border-token-modal-accent-blue'}`}
                                            type='button'
                                        >
                                            <img src={chain.icon} alt={chain.name} className='h-5 w-5 rounded-full' />
                                            <span className='text-[14px] font-medium'>{chain.name}</span>
                                        </button>
                                    ))}
                            </div>
                        </div>
                    </div>

                    {/* Right panel - Tokens with Search - removed border-b from search bar container */}
                    <div className='flex min-h-0 w-full flex-1 flex-col'>
                        {/* Search Bar */}
                        <div className='px-3 pt-1'>
                            <div className='flex w-full items-center gap-2 rounded-md border border-token-modal-border bg-token-modal-input-bg px-3 py-2.5 transition-all duration-200'>
                                <SearchIcon className='h-5 w-5 text-token-modal-input-placeholder' />
                                <input
                                    type='text'
                                    placeholder='Search by symbol, name or address'
                                    value={tokenQuery}
                                    onChange={onTokenSearchChange}
                                    className='w-full bg-transparent text-white placeholder-token-modal-input-placeholder outline-none'
                                />
                            </div>
                        </div>

                        <div className='h-full overflow-y-auto'>
                            {isSearchingAllChains ? (
                                <div className='flex flex-col items-center justify-center gap-4 p-8'>
                                    <div className='flex items-center gap-2'>
                                        <Loader2 className='h-5 w-5 animate-spin' />
                                        <span>Searching all blockchains...</span>
                                    </div>
                                    <SearchProgressBar
                                        completed={searchProgress.completed}
                                        total={searchProgress.total}
                                    />
                                </div>
                            ) : tokenQuery ? (
                                <div className='p-3'>
                                    {activeChainSelection ? (
                                        // When a chain is selected during search, show only tokens from that chain
                                        <div className='animate-fadeIn'>
                                            <div className='px-2 pb-2'>
                                                <h3 className='animate-slideInRight text-sm font-medium text-token-modal-label-text'>
                                                    {`Results for "${tokenQuery}" on ${(activeChainSelection !== undefined && availableChains[activeChainSelection]?.name) || 'selected chain'}`}
                                                </h3>
                                            </div>
                                            <div className='space-y-1'>
                                                {displayedTokens.map(token => {
                                                    const balance = getTokenBalance?.(token)
                                                    const isSelected =
                                                        token.address === selectedToken?.address &&
                                                        token.chainId === selectedToken?.chainId

                                                    return (
                                                        <button
                                                            key={`token-${token.chainId}-${token.address}`}
                                                            onClick={() => onTokenClicked(token)}
                                                            className={cn(
                                                                'flex w-full items-center gap-3 rounded-md px-4 py-3.5 transition-colors duration-200 hover:bg-token-modal-chain-hover',
                                                                isSelected &&
                                                                    'border border-token-modal-border bg-token-modal-chain-selected'
                                                            )}
                                                        >
                                                            <ChainToken
                                                                tokenLogo={
                                                                    token.name === 'BitTorrent'
                                                                        ? '/assets/token/btt.png'
                                                                        : token.logoURI
                                                                }
                                                                networkIcon={`/assets/network/${getChainNameFromChainId(token.chainId)}.png`}
                                                                tokenIconSize={36}
                                                                chainIconSize={18}
                                                            />
                                                            <div className='flex flex-1 flex-col items-start gap-0.5 overflow-hidden'>
                                                                <span className='text-left text-[16px] font-bold'>
                                                                    {token.symbol}
                                                                </span>
                                                                <span className='overflow-hidden text-ellipsis text-left text-sm text-token-modal-label-text'>
                                                                    {token.name}
                                                                </span>
                                                            </div>
                                                            <div className='flex items-center gap-2'>
                                                                {balance?.tokenBalance && (
                                                                    <div className='flex flex-col items-end'>
                                                                        {balance?.usdBalance && (
                                                                            <span className='font-medium'>
                                                                                {balance.usdBalance.startsWith('<')
                                                                                    ? balance.usdBalance
                                                                                    : `$${formatNumber(balance.usdBalance)}`}
                                                                            </span>
                                                                        )}
                                                                        <span className='text-sm text-token-modal-label-text'>
                                                                            {balance.tokenBalance}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {isSelected && (
                                                                    <div className='h-2 w-2 shrink-0 rounded-full bg-token-modal-accent-blue' />
                                                                )}
                                                            </div>
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    ) : (
                                        // Show all search results across chains
                                        <div className='animate-fadeIn'>
                                            <CompactTokensList
                                                tokens={searchResult}
                                                selectedToken={selectedToken}
                                                onTokenClicked={onTokenClicked}
                                                getTokenBalance={getTokenBalance}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className='animate-fadeIn space-y-4 p-3'>
                                    {/* Tokens with balances */}
                                    {displayedTokens.some(
                                        token => token.userBalanceInUSD && token.userBalanceInUSD > 0
                                    ) && (
                                        <div className='space-y-1'>
                                            <div className='px-2'>
                                                <h3 className='animate-slideInRight text-sm font-medium text-token-modal-label-text'>
                                                    Your Tokens
                                                </h3>
                                            </div>
                                            <CompactTokensList
                                                tokens={displayedTokens
                                                    .filter(
                                                        token => token.userBalanceInUSD && token.userBalanceInUSD > 0
                                                    )
                                                    .sort((a, b) => {
                                                        // First prioritize gas tokens
                                                        const gasTokenComparison = compareGasTokens(a, b)
                                                        if (gasTokenComparison !== 0) return gasTokenComparison

                                                        // Then sort by USD balance
                                                        return (b.userBalanceInUSD ?? 0) - (a.userBalanceInUSD ?? 0)
                                                    })}
                                                selectedToken={selectedToken}
                                                onTokenClicked={onTokenClicked}
                                                getTokenBalance={getTokenBalance}
                                            />
                                        </div>
                                    )}

                                    {/* All tokens */}
                                    <div className='space-y-1'>
                                        <div className='px-2'>
                                            <h3 className='animate-slideInRight text-sm font-medium text-token-modal-label-text'>
                                                All Tokens
                                            </h3>
                                        </div>
                                        <CompactTokensList
                                            tokens={displayedTokens
                                                .filter(
                                                    token => !token.userBalanceInUSD || token.userBalanceInUSD === 0
                                                )
                                                .sort((a, b) => {
                                                    // First prioritize gas tokens
                                                    const gasTokenComparison = compareGasTokens(a, b)
                                                    if (gasTokenComparison !== 0) return gasTokenComparison

                                                    // Then sort by market cap
                                                    const aCap = a.market_cap ?? -Infinity
                                                    const bCap = b.market_cap ?? -Infinity
                                                    return bCap - aCap
                                                })}
                                            selectedToken={selectedToken}
                                            onTokenClicked={onTokenClicked}
                                            getTokenBalance={getTokenBalance}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default SelectTokenModal
